import React, { useEffect, useMemo, useState, useRef, Fragment, createElement, Component } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DownloadIcon, CalendarIcon, ChevronRightIcon, ChevronDownIcon, FileTextIcon, InfoIcon } from 'lucide-react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
type PayoutStatus = 'pending' | 'submitted' | 'partially_submitted' | 'failed' | 'cancelled';
type TransferStatus = 'pending' | 'submitted' | 'failed' | 'cancelled';
interface PayoutTransfer {
  transferId: string;
  status: TransferStatus;
  grossAmount: number;
  totalDeductions: number;
  netAmount: number;
  bankReference: string;
  description: string;
}
interface Payout {
  payoutId: string;
  merchantEntity: string;
  status: PayoutStatus;
  effectiveDate: string;
  currency: string;
  grossAmount: number;
  totalDeductions: number;
  netAmount: number;
  destinationAccount: string;
  transfers: PayoutTransfer[];
}
// Raw transfer data (legacy format)
interface RawTransferEntry {
  merchantEntity: string;
  payoutId: string;
  payoutStatus: 'Submitted' | 'Pending' | 'Failed';
  payoutEffectiveDate: string;
  currency: string;
  grossAmount: number;
  totalDeductions: number;
  netAmount: number;
  destinationAccount: string;
  description: string;
  externalPayoutReference: string;
}
const rawTransfers: RawTransferEntry[] = [{
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'up_pending_may6_2026_001',
  payoutStatus: 'Pending',
  payoutEffectiveDate: 'May 6, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 46630.72,
  totalDeductions: 1399.92,
  netAmount: 45230.8,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: 'Upcoming payout for 6 May 2026',
  externalPayoutReference: 'MBB-20260506-TXN-00185'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '69a608748649da0d81711cc',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 7, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 3 for 7 May 2026',
  externalPayoutReference: 'MBB-20260507-TXN-00184'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '69a608748649da0d81712dd',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 7, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 3 for 7 May 2026',
  externalPayoutReference: 'MBB-20260507-TXN-00184'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '69a608748649da0d81713ee',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 7, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1847300.0,
  totalDeductions: 53572.7,
  netAmount: 1793727.3,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '3 of 3 for 7 May 2026',
  externalPayoutReference: 'MBB-20260507-TXN-00184'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '7a1b2c3d4e5f60718a9b0c1',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 6, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1245080.0,
  totalDeductions: 36107.32,
  netAmount: 1208972.68,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 6 May 2026',
  externalPayoutReference: 'MBB-20260506-TXN-00183'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '8b2c3d4e5f6a71829c0d1e2',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 5, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 3 for 5 May 2026',
  externalPayoutReference: 'MBB-20260505-TXN-00179'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '8b2c3d4e5f6a71829c0d2f3',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 5, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 3 for 5 May 2026',
  externalPayoutReference: 'MBB-20260505-TXN-00179'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '8b2c3d4e5f6a71829c0d3g4',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 5, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 210500.0,
  totalDeductions: 6104.5,
  netAmount: 204395.5,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '3 of 3 for 5 May 2026',
  externalPayoutReference: 'MBB-20260505-TXN-00179'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '9c3d4e5f6g7h81930k1l2m3',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 4, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 875000.0,
  totalDeductions: 25375.0,
  netAmount: 849625.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 4 May 2026',
  externalPayoutReference: 'MBB-20260504-TXN-00175'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: '0d4e5f6g7h8i92041l2m3n4',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 3, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 567120.0,
  totalDeductions: 16446.48,
  netAmount: 550673.52,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 3 May 2026',
  externalPayoutReference: 'MBB-20260503-TXN-00172'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'ae5f6g7h8i9j03152m3n4o5',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 2, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1530000.0,
  totalDeductions: 44370.0,
  netAmount: 1485630.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 2 for 2 May 2026',
  externalPayoutReference: 'MBB-20260502-TXN-00168'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'bf6g7h8i9j0k14263n4o5p6',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 2, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 980500.0,
  totalDeductions: 28434.5,
  netAmount: 952065.5,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 2 for 2 May 2026',
  externalPayoutReference: 'MBB-20260502-TXN-00168'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'cg7h8i9j0k1l25374o5p6q7',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 1, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 3 for 1 May 2026',
  externalPayoutReference: 'MBB-20260501-TXN-00165'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'dh8i9j0k1l2m36485p6q7r8',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 1, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 3 for 1 May 2026',
  externalPayoutReference: 'MBB-20260501-TXN-00165'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'ei9j0k1l2m3n47596q7r8s9',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'May 1, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 742650.0,
  totalDeductions: 21536.85,
  netAmount: 721113.15,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '3 of 3 for 1 May 2026',
  externalPayoutReference: 'MBB-20260501-TXN-00165'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'fj0k1l2m3n4o586a7r8s9t0',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 29, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1125000.0,
  totalDeductions: 32625.0,
  netAmount: 1092375.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 29 Apr 2026',
  externalPayoutReference: 'MBB-20260429-TXN-00160'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'gk1l2m3n4o5p697b8s9t0u1',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 27, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1890000.0,
  totalDeductions: 54810.0,
  netAmount: 1835190.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 2 for 27 Apr 2026',
  externalPayoutReference: 'MBB-20260427-TXN-00155'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'hl2m3n4o5p6q708c9t0u1v2',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 27, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 435200.0,
  totalDeductions: 12620.8,
  netAmount: 422579.2,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 2 for 27 Apr 2026',
  externalPayoutReference: 'MBB-20260427-TXN-00155'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'im3n4o5p6q7r819d0u1v2w3',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 25, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 650000.0,
  totalDeductions: 18850.0,
  netAmount: 631150.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 25 Apr 2026',
  externalPayoutReference: 'MBB-20260425-TXN-00150'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'jn4o5p6q7r8s920e1v2w3x4',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 23, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 2000000.0,
  totalDeductions: 58000.0,
  netAmount: 1942000.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 2 for 23 Apr 2026',
  externalPayoutReference: 'MBB-20260423-TXN-00145'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'ko5p6q7r8s9t031f2w3x4y5',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 23, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 315700.0,
  totalDeductions: 9155.3,
  netAmount: 306544.7,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 2 for 23 Apr 2026',
  externalPayoutReference: 'MBB-20260423-TXN-00145'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'lp6q7r8s9t0u142g3x4y5z6',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 21, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1450000.0,
  totalDeductions: 42050.0,
  netAmount: 1407950.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 21 Apr 2026',
  externalPayoutReference: 'MBB-20260421-TXN-00140'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'mq7r8s9t0u1v253h4y5z6a7',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 19, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 780000.0,
  totalDeductions: 22620.0,
  netAmount: 757380.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 1 for 19 Apr 2026',
  externalPayoutReference: 'MBB-20260419-TXN-00135'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'nr8s9t0u1v2w364i5z6a7b8',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 16, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 1680000.0,
  totalDeductions: 48720.0,
  netAmount: 1631280.0,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '1 of 2 for 16 Apr 2026',
  externalPayoutReference: 'MBB-20260416-TXN-00130'
}, {
  merchantEntity: 'Maybank Merchant Sdn. Bhd.',
  payoutId: 'os9t0u1v2w3x475j6a7b8c9',
  payoutStatus: 'Submitted',
  payoutEffectiveDate: 'Apr 16, 2026 15:00:00 MYT',
  currency: getTenant().currency.code,
  grossAmount: 520370.0,
  totalDeductions: 15090.73,
  netAmount: 505279.27,
  destinationAccount: 'KASIKORNBANK •••• 1005',
  description: '2 of 2 for 16 Apr 2026',
  externalPayoutReference: 'MBB-20260416-TXN-00130'
}];
const PAGE_SIZE = 9;
const formatCurrency = (amount: number) => {
  return formatCurrencyAmount(amount, {
    fractionDigits: 2
  });
};
// Transform raw transfers into grouped payouts
function transformToPayouts(transfers: RawTransferEntry[]): Payout[] {
  const grouped = new Map<string, RawTransferEntry[]>();
  transfers.forEach(t => {
    const key = t.externalPayoutReference;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(t);
  });
  const payouts: Payout[] = [];
  let payoutCounter = 10001;
  // Special cases for status variety
  const specialStatuses = new Map<string, PayoutStatus>([['MBB-20260505-TXN-00179', 'partially_submitted']]);
  Array.from(grouped.entries()).forEach(([bankRef, groupTransfers]) => {
    const firstTransfer = groupTransfers[0];
    const grossAmount = groupTransfers.reduce((sum, t) => sum + t.grossAmount, 0);
    const totalDeductions = groupTransfers.reduce((sum, t) => sum + t.totalDeductions, 0);
    const netAmount = groupTransfers.reduce((sum, t) => sum + t.netAmount, 0);
    // Use raw payoutStatus as source of truth, with specialStatuses as override
    const rawStatus = firstTransfer.payoutStatus.toLowerCase() as PayoutStatus;
    let status: PayoutStatus = specialStatuses.get(bankRef) || rawStatus;
    const transfers: PayoutTransfer[] = groupTransfers.map((t, idx) => {
      let transferStatus: TransferStatus = 'submitted';
      if (status === 'partially_submitted' && idx === groupTransfers.length - 1) {
        transferStatus = 'failed';
      }
      if (status === 'pending') {
        transferStatus = 'pending';
      }
      return {
        transferId: `pt_${String(idx + 1).padStart(3, '0')}`,
        status: transferStatus,
        grossAmount: t.grossAmount,
        totalDeductions: t.totalDeductions,
        netAmount: t.netAmount,
        bankReference: `${t.externalPayoutReference}-${String(idx + 1).padStart(2, '0')}`,
        description: t.description
      };
    });
    payouts.push({
      payoutId: `po_${payoutCounter++}`,
      merchantEntity: firstTransfer.merchantEntity,
      status,
      effectiveDate: firstTransfer.payoutEffectiveDate,
      currency: firstTransfer.currency,
      grossAmount,
      totalDeductions,
      netAmount,
      destinationAccount: firstTransfer.destinationAccount,
      transfers
    });
  });
  payouts.sort((a, b) => {
    const dateA = new Date(a.effectiveDate);
    const dateB = new Date(b.effectiveDate);
    return dateB.getTime() - dateA.getTime();
  });
  return payouts;
}
function getPayoutStatusBadge(status: PayoutStatus) {
  const styles: Record<PayoutStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    partially_submitted: 'bg-[var(--colors-warning-light)] text-[var(--colors-warning-dark)] border-[var(--colors-warning-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<PayoutStatus, string> = {
    submitted: 'Submitted',
    partially_submitted: 'Partially submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-2996">
      {labels[status]}
    </span>;
}
function getTransferStatusBadge(status: TransferStatus) {
  const styles: Record<TransferStatus, string> = {
    submitted: 'bg-[var(--colors-info-light)] text-[var(--colors-info-dark)] border-[var(--colors-info-default)]',
    failed: 'bg-[var(--colors-error-light)] text-[var(--colors-error-dark)] border-[var(--colors-error-default)]',
    cancelled: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]',
    pending: 'bg-[var(--colors-neutral-50)] text-[var(--colors-neutral-600)] border-[var(--colors-neutral-200)]'
  };
  const labels: Record<TransferStatus, string> = {
    submitted: 'Submitted',
    failed: 'Failed',
    cancelled: 'Cancelled',
    pending: 'Pending'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`} data-id="element-2997">
      {labels[status]}
    </span>;
}
// Shared CSV escape utility
function escCsv(val: string | number): string {
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function downloadBlob(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
// ─── Report 1: Payout Summary (one row per payout) ───
function downloadPayoutSummaryCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Merchant Entity', 'Payout Status', 'Effective Date', 'Currency', 'Gross Amount', 'Total Deductions', 'Net Amount', 'Destination Account', 'Number of Transfers'];
  const rows = payouts.map(p => [escCsv(p.payoutId), escCsv(p.merchantEntity), escCsv(p.status), escCsv(p.effectiveDate), escCsv(p.currency), p.grossAmount.toFixed(2), p.totalDeductions.toFixed(2), p.netAmount.toFixed(2), escCsv(p.destinationAccount), p.transfers.length].join(','));
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-summary-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report 2: Payout Transfer Report (one row per bank transfer) ───
function downloadPayoutTransferCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Transfer ID', 'Transfer Status', 'Effective Date', 'Currency', 'Gross Amount', 'Total Deductions', 'Net Amount', 'Bank Reference', 'Destination Account', 'Description'];
  const rows: string[] = [];
  payouts.forEach(p => {
    p.transfers.forEach(t => {
      rows.push([escCsv(p.payoutId), escCsv(t.transferId), escCsv(t.status), escCsv(p.effectiveDate), escCsv(p.currency), t.grossAmount.toFixed(2), t.totalDeductions.toFixed(2), t.netAmount.toFixed(2), escCsv(t.bankReference), escCsv(p.destinationAccount), escCsv(t.description)].join(','));
    });
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-transfers-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report 3: Payout Detail Report (one row per transaction line item) ───
interface PayoutTransaction {
  activityType: 'Payment' | 'Refund' | 'Fee' | 'Chargeback' | 'Adjustment';
  sourceActivityId: string;
  currency: string;
  grossAmount: number;
  fee: number;
  netAmount: number;
  description: string;
  orderId: string;
  merchantReferenceId: string;
}
function generateMockTransactions(payout: Payout): PayoutTransaction[] {
  const seed = payout.payoutId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = (i: number) => {
    const x = Math.sin(seed + i) * 10000;
    return x - Math.floor(x);
  };
  const txns: PayoutTransaction[] = [];
  let remainingGross = payout.grossAmount;
  let remainingFees = payout.totalDeductions;
  const descs = ['Weekly grocery order - fresh produce', 'Bulk rice and noodles purchase', 'Seafood platter catering order', 'Office lunch delivery - 25 pax', 'Monthly subscription box - premium', 'Fruit basket gift set x3', 'Restaurant supply restock', 'Organic vegetables wholesale', 'Frozen goods monthly order', 'Beverage supply - soft drinks', 'Bakery ingredients wholesale', 'Meat and poultry weekly supply', 'Dairy products - milk and cheese', 'Cooking oil and condiments restock', 'Hotel breakfast buffet supply', 'School canteen weekly provisions'];
  const count = 8 + Math.floor(rng(0) * 8);
  const base = payout.grossAmount / count;
  for (let i = 0; i < count; i++) {
    const isLast = i === count - 1;
    const gross = isLast ? remainingGross : Math.round(base * (0.5 + rng(i + 10)) * 100) / 100;
    const actualGross = Math.max(100, Math.min(gross, remainingGross));
    const fee = isLast ? remainingFees : Math.round(actualGross * (0.029 + rng(i + 50) * 0.005) * 100) / 100;
    remainingGross = Math.round((remainingGross - actualGross) * 100) / 100;
    remainingFees = Math.round((remainingFees - fee) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 20) * 90000));
    txns.push({
      activityType: 'Payment',
      sourceActivityId: `pay_${num}`,
      currency: payout.currency,
      grossAmount: actualGross,
      fee,
      netAmount: Math.round((actualGross - fee) * 100) / 100,
      description: descs[Math.floor(rng(i + 60) * descs.length)],
      orderId: `ord_${String(10000 + Math.floor(rng(i + 30) * 90000))}`,
      merchantReferenceId: `MR-${String(1000 + Math.floor(rng(i + 40) * 9000))}`
    });
  }
  // Add 1-2 refunds
  const refundCount = 1 + Math.floor(rng(100) * 2);
  for (let i = 0; i < refundCount; i++) {
    const amt = Math.round((500 + rng(i + 200) * 5000) * 100) / 100;
    const num = String(10000 + Math.floor(rng(i + 210) * 90000));
    txns.push({
      activityType: 'Refund',
      sourceActivityId: `rf_${num}`,
      currency: payout.currency,
      grossAmount: -amt,
      fee: 0,
      netAmount: -amt,
      description: 'Refund - customer return',
      orderId: txns[Math.floor(rng(i + 220) * Math.min(3, txns.length))]?.orderId || `ord_${num}`,
      merchantReferenceId: txns[0]?.merchantReferenceId || `MR-${num}`
    });
  }
  return txns;
}
function downloadPayoutDetailCsv(payouts: Payout[]) {
  const headers = ['Payout ID', 'Activity Type', 'Source Activity ID', 'Currency', 'Gross Amount', 'Fee', 'Net Amount', 'Description', 'Order ID', 'Merchant Reference ID'];
  const rows: string[] = [];
  payouts.forEach(p => {
    const txns = generateMockTransactions(p);
    txns.forEach(t => {
      rows.push([escCsv(p.payoutId), escCsv(t.activityType), escCsv(t.sourceActivityId), escCsv(t.currency), t.grossAmount.toFixed(2), t.fee.toFixed(2), t.netAmount.toFixed(2), escCsv(t.description), escCsv(t.orderId), escCsv(t.merchantReferenceId)].join(','));
    });
  });
  const brandName = getTenant().brand.name.toLowerCase();
  downloadBlob(`${brandName}-payout-detail-${new Date().toISOString().slice(0, 10)}.csv`, '\uFEFF' + [headers.join(','), ...rows].join('\n'));
}
// ─── Report Dropdown Component ───
function ReportDropdown({
  label,
  onSummary,
  onTransfer,
  onDetail
}: {
  label: string;
  onSummary: () => void;
  onTransfer: () => void;
  onDetail: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return <div ref={ref} className="relative inline-block" data-id="element-2998">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-2999">
        <DownloadIcon size={12} data-id="element-3000" />
        {label}
        <ChevronDownIcon size={10} data-id="element-3001" />
      </button>

      <AnimatePresence data-id="element-3002">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.12
      }} className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-id="element-3003">
            <button onClick={() => {
          onSummary();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3004">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3005" />
              <div data-id="element-3006">
                <div className="font-medium" data-id="element-3007">Payout summary</div>
                <div className="text-xs text-gray-500" data-id="element-3008">One row per payout</div>
              </div>
            </button>
            <button onClick={() => {
          onTransfer();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3009">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3010" />
              <div data-id="element-3011">
                <div className="font-medium" data-id="element-3012">Transfer report</div>
                <div className="text-xs text-gray-500" data-id="element-3013">
                  One row per bank transfer
                </div>
              </div>
            </button>
            <button onClick={() => {
          onDetail();
          setOpen(false);
        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5" data-id="element-3014">
              <FileTextIcon size={14} className="text-gray-400" data-id="element-3015" />
              <div data-id="element-3016">
                <div className="font-medium" data-id="element-3017">Payout detail</div>
                <div className="text-xs text-gray-500" data-id="element-3018">
                  One row per transaction
                </div>
              </div>
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function StatusTooltipHeader() {
  const [isVisible, setIsVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  useEffect(() => {
    if (isVisible && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
  }, [isVisible]);
  return <div className="flex items-center gap-1.5" data-id="element-3019">
      Status
      <button ref={buttonRef} type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onFocus={() => setIsVisible(true)} onBlur={() => setIsVisible(false)} data-id="element-3020">
        <InfoIcon size={13} data-id="element-3021" />
      </button>
      {isVisible && createPortal(<div style={{
      top: pos.top,
      left: pos.left
    }} className="fixed -translate-x-1/2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-[9999] font-normal normal-case tracking-normal leading-relaxed pointer-events-none" data-id="element-3022">
            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3023">
              Payout statuses
            </p>
            <ul className="space-y-1.5 mb-3" data-id="element-3024">
              <li data-id="element-3025">
                <span className="font-medium text-white" data-id="element-3026">Pending</span> —
                Created but not yet submitted to the bank
              </li>
              <li data-id="element-3027">
                <span className="font-medium text-white" data-id="element-3028">Submitted</span> — All
                transfers submitted to the bank
              </li>
              <li data-id="element-3029">
                <span className="font-medium text-white" data-id="element-3030">
                  Partially submitted
                </span>{' '}
                — Some transfers submitted, others pending or failed
              </li>
              <li data-id="element-3031">
                <span className="font-medium text-white" data-id="element-3032">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3033">
                <span className="font-medium text-white" data-id="element-3034">Cancelled</span> —
                Cancelled before submission completed
              </li>
            </ul>

            <p className="font-semibold mb-2 text-[11px] uppercase tracking-wider text-gray-300" data-id="element-3035">
              Transfer statuses
            </p>
            <ul className="space-y-1.5" data-id="element-3036">
              <li data-id="element-3037">
                <span className="font-medium text-white" data-id="element-3038">Pending</span> —
                Created but not yet submitted
              </li>
              <li data-id="element-3039">
                <span className="font-medium text-white" data-id="element-3040">Submitted</span> —
                Instruction submitted to the bank
              </li>
              <li data-id="element-3041">
                <span className="font-medium text-white" data-id="element-3042">Failed</span> —
                Submission failed
              </li>
              <li data-id="element-3043">
                <span className="font-medium text-white" data-id="element-3044">Cancelled</span> —
                Cancelled before submission
              </li>
            </ul>
          </div>, document.body)}
    </div>;
}
export function Payouts() {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  const allPayouts = useMemo(() => transformToPayouts(rawTransfers), []);
  const totalEntries = allPayouts.length;
  const totalPages = Math.ceil(totalEntries / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalEntries);
  const currentEntries = allPayouts.slice(startIndex, endIndex);
  const last7DaysPayouts = allPayouts.filter(p => {
    const dateStr = p.effectiveDate;
    return dateStr.includes('May 7') || dateStr.includes('May 6') || dateStr.includes('May 5') || dateStr.includes('May 4') || dateStr.includes('May 3') || dateStr.includes('May 2') || dateStr.includes('May 1');
  });
  const last7Total = last7DaysPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const last30Total = allPayouts.reduce((sum, p) => sum + p.grossAmount, 0);
  const toggleExpand = (payoutId: string) => {
    setExpandedPayouts(prev => {
      const next = new Set(prev);
      if (next.has(payoutId)) {
        next.delete(payoutId);
      } else {
        next.add(payoutId);
      }
      return next;
    });
  };
  return <div className="max-w-[1600px] mx-auto" data-id="element-3045">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-3046">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-3047">Payouts</h1>
        <ReportDropdown label="Export" onSummary={() => downloadPayoutSummaryCsv(allPayouts)} onTransfer={() => downloadPayoutTransferCsv(allPayouts)} onDetail={() => downloadPayoutDetailCsv(allPayouts)} data-id="element-3048" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-3049">
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3050">
          <div className="flex items-center justify-between mb-3" data-id="element-3051">
            <span className="text-sm text-gray-500" data-id="element-3052">Next 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3053" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3054">
            {formatCurrency(allPayouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.grossAmount, 0))}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3055">
            {allPayouts.filter(p => p.status === 'pending').length} upcoming
            payout
            {allPayouts.filter(p => p.status === 'pending').length !== 1 ? 's' : ''}{' '}
            · May 6 – May 12, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3056">
          <div className="flex items-center justify-between mb-3" data-id="element-3057">
            <span className="text-sm text-gray-500" data-id="element-3058">Last 7 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3059" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3060">
            {formatCurrency(last7Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3061">
            {last7DaysPayouts.length} payouts · May 1 – May 7, 2026
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-3062">
          <div className="flex items-center justify-between mb-3" data-id="element-3063">
            <span className="text-sm text-gray-500" data-id="element-3064">Last 30 days</span>
            <CalendarIcon size={16} className="text-gray-400" data-id="element-3065" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1" data-id="element-3066">
            {formatCurrency(last30Total)}
          </div>
          <div className="text-sm text-gray-500" data-id="element-3067">
            {allPayouts.length} payouts · Apr 7 – May 6, 2026
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-3068">
        <div className="overflow-x-auto" data-id="element-3069">
          <table className="w-full" data-id="element-3070">
            <thead data-id="element-3071">
              <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-3072">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap w-10" data-id="element-3073"></th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3074">
                  Payout ID
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3075">
                  <StatusTooltipHeader data-id="element-3076" />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3077">
                  Effective date
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3078">
                  Currency
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3079">
                  Gross amount
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3080">
                  Deductions
                </th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3081">
                  Net amount
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3082">
                  Destination
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3083">
                  Transfers
                </th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-3084">
                  Report
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-3085">
              {currentEntries.map(payout => {
              const isExpanded = expandedPayouts.has(payout.payoutId);
              return <Fragment key={payout.payoutId} data-id="element-3086">
                    {/* Payout Row */}
                    <tr className={`hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50/30' : ''}`} data-id="element-3087">
                      <td className="px-5 py-4" data-id="element-3088">
                        <button onClick={() => toggleExpand(payout.payoutId)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-3089">
                          <motion.div animate={{
                        rotate: isExpanded ? 90 : 0
                      }} transition={{
                        duration: 0.15
                      }} data-id="element-3090">
                            <ChevronRightIcon size={16} data-id="element-3091" />
                          </motion.div>
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-mono font-medium whitespace-nowrap" data-id="element-3092">
                        {payout.payoutId}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap" data-id="element-3093">
                        {getPayoutStatusBadge(payout.status)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3094">
                        {payout.effectiveDate}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3095">
                        {payout.currency}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 text-right whitespace-nowrap" data-id="element-3096">
                        {formatCurrency(payout.grossAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 text-right whitespace-nowrap" data-id="element-3097">
                        {formatCurrency(payout.totalDeductions)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-900 font-semibold text-right whitespace-nowrap" data-id="element-3098">
                        {formatCurrency(payout.netAmount)}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap" data-id="element-3099">
                        {payout.destinationAccount}
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3100">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600" data-id="element-3101">
                          {payout.transfers.length}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center whitespace-nowrap" data-id="element-3102">
                        <button onClick={() => downloadPayoutDetailCsv([payout])} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors" data-id="element-3103">
                          <DownloadIcon size={12} data-id="element-3104" />
                          .csv
                        </button>
                      </td>
                    </tr>

                    {/* Transfer Sub-Rows */}
                    <AnimatePresence data-id="element-3105">
                      {isExpanded && payout.transfers.map((transfer, idx) => <motion.tr key={transfer.transferId} initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.15,
                    delay: idx * 0.03
                  }} className="bg-[var(--colors-neutral-50)] border-l-2 border-l-[var(--colors-border-default)]" data-id="element-3106">
                            <td className="px-5 py-3" data-id="element-3107"></td>
                            <td className="px-5 py-3 text-sm font-mono text-[var(--colors-text-secondary)] whitespace-nowrap" data-id="element-3108">
                              {transfer.transferId}
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap" data-id="element-3109">
                              {getTransferStatusBadge(transfer.status)}
                            </td>
                            <td className="px-5 py-3" data-id="element-3110"></td>
                            <td className="px-5 py-3" data-id="element-3111"></td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-secondary)] text-right whitespace-nowrap" data-id="element-3112">
                              {formatCurrency(transfer.grossAmount)}
                            </td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-tertiary)] text-right whitespace-nowrap" data-id="element-3113">
                              {formatCurrency(transfer.totalDeductions)}
                            </td>
                            <td className="px-5 py-3 text-sm text-[var(--colors-text-primary)] font-medium text-right whitespace-nowrap" data-id="element-3114">
                              {formatCurrency(transfer.netAmount)}
                            </td>
                            <td className="px-5 py-3" data-id="element-3115"></td>
                            <td className="px-5 py-3 text-xs font-mono text-[var(--colors-text-tertiary)] whitespace-nowrap" data-id="element-3116">
                              {transfer.bankReference}
                            </td>
                            <td className="px-5 py-3" data-id="element-3117"></td>
                          </motion.tr>)}
                    </AnimatePresence>
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200" data-id="element-3118">
          <span className="text-sm text-gray-500" data-id="element-3119">
            Showing {startIndex + 1} to {endIndex} of {totalEntries} payouts
          </span>
          <div className="flex items-center gap-2" data-id="element-3120">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3121">
              Previous
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-3122">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}